import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, TrendingDown, Calendar, DollarSign,
  Search, ArrowUpDown, Eye, AlertTriangle, Plus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthProvider';
import Modal from '../components/ui/Modal';
import AddClientForm from '../components/AddClientForm';
import { getClientDisplayName } from '../lib/clientUtils';

interface ClientCSMData {
  id: string;
  name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  end_date: string | null;
  days_per_month: number | null;
  monthly_price: number | null;
  contract_type: string | null;
  active_campaigns: number;
  rdv_this_month: number;
  last_session_date: string | null;
}

type FilterType = 'all' | 'expiring' | 'no_campaign' | 'low_rdv';
type SortField = 'name' | 'end_date' | 'active_campaigns' | 'rdv_this_month' | 'last_session_date';

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [clients, setClients] = useState<ClientCSMData[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientCSMData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);

  useEffect(() => {
    if (profile) {
      if (profile.role === 'client') {
        navigate('/');
        return;
      }
      fetchClientsData();
    }
  }, [profile, navigate]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [clients, searchQuery, activeFilter, sortField, sortDirection]);

  const fetchClientsData = async () => {
    setIsLoading(true);
    try {
      if (!profile) {
        setClients([]);
        return;
      }

      let clientsData: ClientCSMData[] = [];

      if (profile.role === 'client') {
        const { data: profileClients, error: pcError } = await supabase
          .from('profile_clients')
          .select('client_id')
          .eq('profile_id', profile.user_id);

        if (pcError) throw pcError;

        const clientIds = profileClients?.map(pc => pc.client_id) || [];

        if (clientIds.length === 0) {
          setClients([]);
          return;
        }

        const { data, error } = await supabase.rpc('get_clients_csm_data');
        if (error) throw error;

        clientsData = (data || []).filter((c: ClientCSMData) => clientIds.includes(c.id));
      } else {
        const { data, error } = await supabase.rpc('get_clients_csm_data');
        if (error) throw error;
        clientsData = data || [];
      }

      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...clients];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.company_name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      );
    }

    if (activeFilter === 'expiring') {
      result = result.filter(c => isExpiringSoon(c.end_date));
    } else if (activeFilter === 'no_campaign') {
      result = result.filter(c => c.active_campaigns === 0);
    } else if (activeFilter === 'low_rdv') {
      result = result.filter(c => c.rdv_this_month < 2);
    }

    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === 'end_date' || sortField === 'last_session_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredClients(result);
  };

  const isExpiringSoon = (endDate: string | null): boolean => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const today = new Date();
    const thirtyDays = new Date();
    thirtyDays.setDate(today.getDate() + 30);
    return end >= today && end <= thirtyDays;
  };

  const isExpired = (endDate: string | null): boolean => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const getExpiringCount = () => clients.filter(c => isExpiringSoon(c.end_date)).length;
  const getNoCampaignCount = () => clients.filter(c => c.active_campaigns === 0).length;
  const getLowRdvCount = () => clients.filter(c => c.rdv_this_month < 2).length;
  const getTotalRevenue = () => clients.reduce((sum, c) => sum + (c.monthly_price || 0), 0);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getDaysSince = (dateStr: string | null): string => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    const today = new Date();
    const diff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Il y a 1 jour';
    return `Il y a ${diff} jours`;
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const lettersOnly = name.replace(/[^A-Za-z\s]/g, '');
    if (lettersOnly.length === 0) return '?';
    const words = lettersOnly.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const getColorForInitials = (name: string) => {
    const colors = [
      'bg-pink-100 text-pink-700',
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-yellow-100 text-yellow-700',
      'bg-red-100 text-red-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E91E8C] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des clients...</p>
        </div>
      </div>
    );
  }

  const handleAddClientSuccess = (newClient: any) => {
    setIsAddClientModalOpen(false);
    fetchClientsData();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion Clients CSM</h1>
            <p className="text-gray-600">Vue d'ensemble et suivi des clients actifs</p>
          </div>
          {profile && (profile.role === 'admin' || profile.role === 'super_admin') && (
            <button
              onClick={() => setIsAddClientModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#EE3195] text-white rounded-lg hover:bg-[#d91e7f] transition-colors font-medium"
            >
              <Plus className="h-5 w-5" />
              Client
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setActiveFilter(activeFilter === 'expiring' ? 'all' : 'expiring')}
            className={`bg-white rounded-xl shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'expiring' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{getExpiringCount()}</h3>
            <p className="text-sm font-medium text-gray-600">Expirent bientôt</p>
            <p className="text-xs text-gray-500 mt-1">Contrats &lt; 30 jours</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => setActiveFilter(activeFilter === 'no_campaign' ? 'all' : 'no_campaign')}
            className={`bg-white rounded-xl shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'no_campaign' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{getNoCampaignCount()}</h3>
            <p className="text-sm font-medium text-gray-600">Sans campagne</p>
            <p className="text-xs text-gray-500 mt-1">Aucune campagne active</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => setActiveFilter(activeFilter === 'low_rdv' ? 'all' : 'low_rdv')}
            className={`bg-white rounded-xl shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'low_rdv' ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{getLowRdvCount()}</h3>
            <p className="text-sm font-medium text-gray-600">RDV faibles</p>
            <p className="text-xs text-gray-500 mt-1">Moins de 2 RDV ce mois</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(getTotalRevenue())}
            </h3>
            <p className="text-sm font-medium text-gray-600">Revenu mensuel</p>
            <p className="text-xs text-gray-500 mt-1">Total MRR</p>
          </motion.div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom, entreprise ou email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === 'all'
                    ? 'bg-[#E91E8C] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setActiveFilter('expiring')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === 'expiring'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Expire bientôt
              </button>
              <button
                onClick={() => setActiveFilter('no_campaign')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === 'no_campaign'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sans campagne
              </button>
              <button
                onClick={() => setActiveFilter('low_rdv')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === 'low_rdv'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                RDV faibles
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Client
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('end_date')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Fin de contrat
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('active_campaigns')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Campagnes
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('rdv_this_month')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      RDV ce mois
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('last_session_date')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Dernière session
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClients.map((client) => {
                  const expiringSoon = isExpiringSoon(client.end_date);
                  const expired = isExpired(client.end_date);

                  return (
                    <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${getColorForInitials(getClientDisplayName(client))}`}>
                            {getInitials(getClientDisplayName(client))}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{getClientDisplayName(client)}</div>
                            {client.name && client.company_name && client.name !== client.company_name && (
                              <div className="text-sm text-gray-500">{client.name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {client.end_date ? (
                          <div className="flex items-center gap-2">
                            {expired ? (
                              <span className="text-sm font-medium text-red-600">Expiré</span>
                            ) : expiringSoon ? (
                              <>
                                <AlertTriangle className="h-4 w-4 text-orange-600" />
                                <span className="text-sm font-medium text-orange-600">{formatDate(client.end_date)}</span>
                              </>
                            ) : (
                              <span className="text-sm text-green-700">{formatDate(client.end_date)}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.active_campaigns > 0
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {client.active_campaigns}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.rdv_this_month >= 2
                            ? 'bg-green-100 text-green-800'
                            : client.rdv_this_month === 1
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {client.rdv_this_month}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{getDaysSince(client.last_session_date)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => navigate(`/clients/${client.id}`)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E91E8C] text-white rounded-lg hover:bg-[#D01A7C] transition-colors text-sm font-medium"
                        >
                          <Eye className="h-4 w-4" />
                          Ouvrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredClients.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun client trouvé</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-500 text-center">
          {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} affiché{filteredClients.length > 1 ? 's' : ''}
          {activeFilter !== 'all' && ` (filtre actif)`}
        </div>
      </div>

      <Modal isOpen={isAddClientModalOpen} onClose={() => setIsAddClientModalOpen(false)} title="Ajouter un client">
        <AddClientForm onSuccess={handleAddClientSuccess} onCancel={() => setIsAddClientModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default ClientsPage;
